import { Row } from '@lib/core/src/styledComponents/StyledComponents';
import { Stack } from '@mui/material';

import { NylasGrantDtoProvider } from '@/api/generated';
import ConnectedIcon from '@/assets/integrations/connected-icon.svg?react';
import GmailLogo from '@/assets/third-party-logos/google/gmail.svg?react';
import GoogleCalendarLogo from '@/assets/third-party-logos/google/google-calendar.svg?react';
import GoogleContactsLogo from '@/assets/third-party-logos/google/google-contacts.svg?react';
import GoogleLogo from '@/assets/third-party-logos/google/google.svg?react';
import MicrosoftCalendarLogo from '@/assets/third-party-logos/microsoft/microsoft-calendar.svg?react';
import MicrosoftContactsLogo from '@/assets/third-party-logos/microsoft/microsoft-contacts.svg?react';
import MicrosoftOutlookLogo from '@/assets/third-party-logos/microsoft/microsoft-outlook.svg?react';
import MicrosoftLogo from '@/assets/third-party-logos/microsoft/microsoft.svg?react';

import {
  CategoryTitle,
  ConnectButton,
  ConnectButtonText,
  IntegrationContainer,
  IntegrationFooter,
  IntegrationLogos,
  IntegrationsCategory,
  IntegrationSmallLogos,
  IntegrationsRow,
  LogoContainer,
  ProviderTitle,
  RequiredText
} from '../IntegrationsStyles';

interface WorkspaceAndCalendarProps {
  hasGoogle: boolean;
  hasMicrosoft: boolean;
  onDisconnect: (provider: NylasGrantDtoProvider) => void;
  connectToGoogle: () => void;
  connectToMicrosoft: () => void;
}

const WorkspaceAndCalendar = ({
  hasGoogle,
  hasMicrosoft,
  onDisconnect,
  connectToGoogle,
  connectToMicrosoft
}: WorkspaceAndCalendarProps) => {
  return (
    <IntegrationsCategory>
      <Row sx={{ gap: '4px' }}>
        <CategoryTitle>Workspaces and Calendar</CategoryTitle>
        {!hasGoogle && !hasMicrosoft && <RequiredText>(Required)</RequiredText>}
      </Row>
      <IntegrationsRow>
        <IntegrationContainer disabled={hasMicrosoft}>
          <IntegrationLogos>
            <LogoContainer>
              <GoogleLogo width="30px" height="30px" />
            </LogoContainer>
            <IntegrationSmallLogos>
              <GmailLogo />
              <GoogleCalendarLogo />
              <GoogleContactsLogo width="14px" height="14px" />
            </IntegrationSmallLogos>
          </IntegrationLogos>

          <IntegrationFooter>
            <Stack direction="row" alignItems="center" gap={1}>
              <ProviderTitle>Google </ProviderTitle>
              {hasGoogle && <ConnectedIcon width="15px" height="15px" />}
            </Stack>

            <ConnectButton
              disabled={hasMicrosoft}
              onClick={() =>
                hasGoogle ? onDisconnect(NylasGrantDtoProvider.google) : connectToGoogle()
              }
            >
              <ConnectButtonText>{hasGoogle ? 'Disconnect' : 'Connect'}</ConnectButtonText>
            </ConnectButton>
          </IntegrationFooter>
        </IntegrationContainer>

        <IntegrationContainer disabled={hasGoogle}>
          <IntegrationLogos>
            <LogoContainer>
              <MicrosoftLogo width="30px" height="30px" />
            </LogoContainer>

            <IntegrationSmallLogos>
              <MicrosoftOutlookLogo />
              <MicrosoftCalendarLogo />
              <MicrosoftContactsLogo width="14px" height="14px" />
            </IntegrationSmallLogos>
          </IntegrationLogos>

          <IntegrationFooter>
            <Stack direction="row" alignItems="center" gap={1}>
              <ProviderTitle>Microsoft</ProviderTitle>
              {hasMicrosoft && <ConnectedIcon width="15px" height="15px" />}
            </Stack>

            <ConnectButton
              disabled={hasGoogle}
              onClick={() =>
                hasMicrosoft ? onDisconnect(NylasGrantDtoProvider.microsoft) : connectToMicrosoft()
              }
            >
              <ConnectButtonText>{hasMicrosoft ? 'Disconnect' : 'Connect'}</ConnectButtonText>
            </ConnectButton>
          </IntegrationFooter>
        </IntegrationContainer>
      </IntegrationsRow>
    </IntegrationsCategory>
  );
};

export default WorkspaceAndCalendar;
